import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CheckCircle2, RefreshCw, TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import useCurrency from '@/Hooks/useCurrency';
import { useBranches, formatDateTime, useQueryParams } from '@/Pages/Operations/shared';
// Canonical decimal-string <-> minor-unit-integer conversion, reused rather
// than reimplemented: a second version that goes through `Number()` on the
// full decimal string (as opposed to splitting on '.' and padding the
// fraction first) is exactly the kind of drift that puts a fil of float error
// into a variance figure. See `Billing/lib.ts`'s own doc comment.
import { toMinor, fromMinor } from '@/Pages/Billing/lib';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Table, TableWrap, TD, TH, THead, TR } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  DELIVERY_PERMISSIONS,
  DELIVERY_ROUTES,
  apiMessage,
  type Driver,
  type DriverShift,
} from '../deliveryApi';
import Money from './Money';
import PanelResult from './PanelResult';

/**
 * ## Driver cash reconciliation
 *
 * Deliberately a separate tab, and a separate component, from
 * `DispatchBoardPanel` — a dispatcher watching live deliveries all shift and
 * a manager reconciling a driver's cash at handover are different people at
 * a different moment, and this component never subscribes to
 * `delivery.status_changed` or renders a delivery row.
 *
 * **No realtime here.** The backend's own `VENUE_EVENTS` table has exactly
 * two entries for this whole epic — `delivery.status_changed` and
 * `delivery.location_updated` — and neither is a driver-shift event: opening,
 * closing and reconciling a shift emit nothing onto the event bus at all.
 * This is also, unlike the board, not a screen meant to be left running all
 * shift; it is opened at handover, on demand, so a manual Refresh — reloaded
 * again after every mutation this panel makes — is the honest choice, not a
 * missing subscription.
 *
 * **Read and write are split by what the backend itself enforces, not by
 * guesswork.** `DriverShiftsService.open()`/`.close()` resolve the caller's
 * OWN driver profile server-side (`requireOwnDriver`) — nobody, however
 * senior, can open or close a shift on another driver's behalf through this
 * API. `findAll` itself decides who sees everyone's shifts on the `drivers.view`
 * key specifically (confirmed by reading `driver-shifts.service.ts`, not
 * assumed): holding it shows every shift, matching what an operator who can
 * also see the driver roster would expect; lacking it scopes the list down to
 * the caller's own shifts, whether or not that caller happens to hold
 * `driver_shifts.view` more broadly. So the split below mirrors the API
 * exactly — a dispatcher/manager (`drivers.view`) gets the full ledger and the
 * `driver_shifts.reconcile` sign-off; anyone else who can act on a shift at
 * all gets the self-service card for their own.
 */
export default function DriverShiftsPanel() {
  const { can } = usePermission();
  const isDispatcher = can(DELIVERY_PERMISSIONS.driversView);
  return isDispatcher ? <ShiftLedger /> : <MyShiftCard />;
}

function varianceTone(minor: number): string {
  if (minor === 0) return 'text-green-700 dark:text-green-400';
  return minor > 0 ? 'text-sky-700 dark:text-sky-400' : 'text-red-600 dark:text-red-400';
}

function ShiftLedger() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const canReconcile = can(DELIVERY_PERMISSIONS.driverShiftsReconcile);

  const [branchId, setBranchId] = useState('');
  const [status, setStatus] = useState('');
  const [onlyUnreconciled, setOnlyUnreconciled] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const params = useQueryParams({ branchId, status, limit: 100 });
  const shiftsQuery = useApi<unknown>(DELIVERY_ROUTES.driverShifts, params);
  const driversQuery = useApi<unknown>(DELIVERY_ROUTES.drivers, useQueryParams({ limit: 200 }));

  const shifts = rowsOf<DriverShift>(shiftsQuery.data).rows;
  const drivers = rowsOf<Driver>(driversQuery.data).rows;
  const driverName = useMemo(() => {
    const map = new Map(
      drivers.map((d) => [d.id, d.user ? `${d.user.firstName} ${d.user.lastName}`.trim() : d.id]),
    );
    return (id: string) => map.get(id) ?? id;
  }, [drivers]);
  const branchName = useMemo(() => {
    const map = new Map(branches.map((b) => [b.id, b.name]));
    return (id: string) => map.get(id) ?? id;
  }, [branches]);

  const rows = onlyUnreconciled
    ? shifts.filter((s) => s.status === 'CLOSED' && !s.reconciledAt)
    : shifts;

  const filtered = Boolean(branchId || status || onlyUnreconciled);
  const clearFilters = () => {
    setBranchId('');
    setStatus('');
    setOnlyUnreconciled(false);
  };

  const reload = useCallback(() => {
    shiftsQuery.reload();
    driversQuery.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shiftsQuery.reload, driversQuery.reload]);

  const reconcile = async (shift: DriverShift) => {
    setBusyId(shift.id);
    try {
      await api.post(DELIVERY_ROUTES.driverShiftReconcile(shift.id));
      toast.success(t('delivery.shifts.reconciled'));
      reload();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.shifts.actionFailed')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {branches.length > 0 && (
          <Select
            className="w-auto min-w-[10rem]"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            aria-label={t('delivery.shifts.branch')}
          >
            <option value="">{t('delivery.shifts.allBranches')}</option>
            {branches.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </Select>
        )}
        <Select
          className="w-auto min-w-[10rem]"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          aria-label={t('delivery.shifts.status')}
        >
          <option value="">{t('delivery.shifts.allStatuses')}</option>
          <option value="OPEN">{t('delivery.shifts.open')}</option>
          <option value="CLOSED">{t('delivery.shifts.closed')}</option>
        </Select>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={onlyUnreconciled}
            onChange={(e) => setOnlyUnreconciled(e.target.checked)}
            className="h-4 w-4 rounded border-line-strong"
          />
          {t('delivery.shifts.onlyUnreconciled')}
        </label>

        <Button className="ms-auto" size="sm" variant="outline" onClick={reload} disabled={shiftsQuery.loading}>
          <Icon as={RefreshCw} />
          {t('delivery.shifts.refresh')}
        </Button>
      </div>

      <PanelResult
        loading={shiftsQuery.loading && !shiftsQuery.data}
        error={shiftsQuery.error}
        count={rows.length}
        onRetry={reload}
        subject={t('delivery.shifts.subject')}
        filtered={filtered}
        onClearFilters={clearFilters}
        emptyTitle={t('delivery.shifts.emptyTitle')}
        emptyHint={t('delivery.shifts.emptyHint')}
      >
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('delivery.shifts.driver')}</TH>
                <TH>{t('delivery.shifts.branch')}</TH>
                <TH>{t('delivery.shifts.status')}</TH>
                <TH>{t('delivery.shifts.opened')}</TH>
                <TH>{t('delivery.shifts.closed')}</TH>
                <TH className="text-end">{t('delivery.shifts.expected')}</TH>
                <TH className="text-end">{t('delivery.shifts.counted')}</TH>
                <TH className="text-end">{t('delivery.shifts.variance')}</TH>
                <TH className="text-end">{t('delivery.shifts.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {rows.map((shift) => (
                <ShiftRow
                  key={shift.id}
                  shift={shift}
                  driverName={driverName(shift.driverId)}
                  branchName={branchName(shift.branchId)}
                  locale={i18n.language}
                  canReconcile={canReconcile}
                  busy={busyId === shift.id}
                  onReconcile={() => void reconcile(shift)}
                />
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </PanelResult>
    </div>
  );
}

function ShiftRow({
  shift,
  driverName,
  branchName,
  locale,
  canReconcile,
  busy,
  onReconcile,
}: {
  shift: DriverShift;
  driverName: string;
  branchName: string;
  locale: string;
  canReconcile: boolean;
  busy: boolean;
  onReconcile: () => void;
}) {
  const { t } = useTranslation();
  const { code } = useCurrency(shift.branchId);
  const varianceMinor = shift.varianceCash !== null ? toMinor(shift.varianceCash, code) : null;
  const unreconciled = shift.status === 'CLOSED' && !shift.reconciledAt;

  return (
    <TR className={unreconciled ? 'bg-amber-50/60 dark:bg-amber-950/20' : undefined}>
      <TD>{driverName}</TD>
      <TD>{branchName}</TD>
      <TD>
        <span
          className={cn(
            'inline-flex rounded px-2 py-0.5 text-xs font-medium',
            shift.status === 'OPEN'
              ? 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300'
              : 'bg-line-strong/30 text-ink-muted',
          )}
        >
          {t(`delivery.shifts.${shift.status === 'OPEN' ? 'open' : 'closed'}`)}
        </span>
      </TD>
      <TD className="text-ink-muted">{formatDateTime(shift.openedAt, locale)}</TD>
      <TD className="text-ink-muted">{formatDateTime(shift.closedAt, locale)}</TD>
      <TD className="text-end">
        <Money amount={shift.liveExpectedCash ?? shift.expectedCash} branchId={shift.branchId} />
      </TD>
      <TD className="text-end">
        {shift.countedCash !== null ? (
          <Money amount={shift.countedCash} branchId={shift.branchId} />
        ) : (
          <span className="text-ink-muted">—</span>
        )}
      </TD>
      <TD className="text-end">
        {/*
          The honest number, always — never a checkmark standing in for it.
          A CLOSED shift with a shortfall renders red here whether or not it
          has been reconciled; `reconciledAt` only ever adds a small "signed
          off" note beside the figure, it never replaces or hides it.
        */}
        {varianceMinor !== null ? (
          <span className={cn('font-medium', varianceTone(varianceMinor))}>
            <Money amount={shift.varianceCash} branchId={shift.branchId} />
          </span>
        ) : (
          <span className="text-ink-muted">—</span>
        )}
        {unreconciled && (
          <span className="ms-2 inline-flex items-center gap-1 text-xs text-amber-800 dark:text-amber-300">
            <Icon as={TriangleAlert} size="xs" />
            {t('delivery.shifts.needsReview')}
          </span>
        )}
        {shift.reconciledAt && (
          <span className="ms-2 inline-flex items-center gap-1 text-xs text-green-700 dark:text-green-400">
            <Icon as={CheckCircle2} size="xs" />
            {t('delivery.shifts.reviewedLabel')}
          </span>
        )}
      </TD>
      <TD className="text-end">
        {unreconciled && canReconcile && (
          <Button size="sm" variant="outline" disabled={busy} onClick={onReconcile}>
            {t('delivery.shifts.reconcile')}
          </Button>
        )}
      </TD>
    </TR>
  );
}

/**
 * A driver's own shift — self-service, opened and closed only against the
 * caller's own driver profile (`requireOwnDriver` on the backend). The
 * counted-vs-expected variance is computed and shown BEFORE the driver ever
 * presses close, the same honesty `Billing/ShiftPanel.tsx` already gives a
 * cashier's till — a driver must see the shortfall on their own screen, not
 * find out about it from a manager later.
 */
function MyShiftCard() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();
  const canOpen = can(DELIVERY_PERMISSIONS.driverShiftsOpen);
  const canClose = can(DELIVERY_PERMISSIONS.driverShiftsClose);

  const shiftQuery = useApi<unknown>(DELIVERY_ROUTES.driverShifts, { status: 'OPEN', limit: 1 });
  const shift = rowsOf<DriverShift>(shiftQuery.data).rows[0] ?? null;

  const { decimals, code } = useCurrency(shift?.branchId ?? null);
  const step = decimals === 0 ? '1' : `0.${'0'.repeat(decimals - 1)}1`;

  const [openBranchId, setOpenBranchId] = useState('');
  const [openingCash, setOpeningCash] = useState('0');
  const [openOpen, setOpenOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [countedCash, setCountedCash] = useState('0');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const openShift = async () => {
    if (!openBranchId) return;
    setSubmitting(true);
    try {
      await api.post(DELIVERY_ROUTES.driverShifts, { branchId: openBranchId, openingCash });
      toast.success(t('delivery.shifts.opened'));
      setOpenOpen(false);
      shiftQuery.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.shifts.actionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const closeShift = async () => {
    if (!shift) return;
    setSubmitting(true);
    try {
      await api.post(DELIVERY_ROUTES.driverShiftClose(shift.id), {
        countedCash,
        notes: notes.trim() || undefined,
      });
      toast.success(t('delivery.shifts.closed'));
      setCloseOpen(false);
      shiftQuery.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('delivery.shifts.actionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const expected = shift?.liveExpectedCash ?? shift?.expectedCash ?? '0';
  const varianceMinor = toMinor(countedCash, code) - toMinor(expected, code);

  return (
    <div className="max-w-xl space-y-4">
      <PanelResult
        loading={shiftQuery.loading && !shiftQuery.data}
        error={shiftQuery.error}
        count={shift ? 1 : 0}
        onRetry={shiftQuery.reload}
        subject={t('delivery.shifts.mySubject')}
        emptyTitle={t('delivery.shifts.noneOpen')}
        emptyHint={canOpen ? undefined : t('delivery.shifts.noOpenPermission')}
        emptyAction={
          canOpen && (
            <Button onClick={() => setOpenOpen(true)}>{t('delivery.shifts.openShift')}</Button>
          )
        }
      >
        <section className="space-y-3 rounded-xl border bg-surface p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{t('delivery.shifts.myShift')}</p>
              <p className="text-xs text-ink-muted">
                {t('delivery.shifts.openSince', { time: formatDateTime(shift?.openedAt, i18n.language) })}
              </p>
            </div>
            <span className="text-lg font-semibold tabular-nums">
              <Money amount={expected} branchId={shift?.branchId ?? ''} />
            </span>
          </div>
          <p className="text-xs text-ink-muted">{t('delivery.shifts.expectedHint')}</p>
          {canClose && (
            <Button
              variant="secondary"
              onClick={() => {
                setCountedCash(expected);
                setCloseOpen(true);
              }}
            >
              {t('delivery.shifts.closeShift')}
            </Button>
          )}
        </section>
      </PanelResult>

      {openOpen && (
        <Dialog
          open
          onClose={() => setOpenOpen(false)}
          title={t('delivery.shifts.openShift')}
          description={t('delivery.shifts.openHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setOpenOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={openShift} disabled={submitting || !openBranchId}>
                {t('delivery.shifts.openShift')}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Field label={t('delivery.shifts.branch')} required>
              <Select value={openBranchId} onChange={(e) => setOpenBranchId(e.target.value)}>
                <option value="">{t('delivery.shifts.branchPlaceholder')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label={t('delivery.shifts.openingCash')}>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                value={openingCash}
                onChange={(e) => setOpeningCash(e.target.value)}
              />
            </Field>
          </div>
        </Dialog>
      )}

      {closeOpen && shift && (
        <Dialog
          open
          onClose={() => setCloseOpen(false)}
          title={t('delivery.shifts.closeShift')}
          description={t('delivery.shifts.closeHint')}
          footer={
            <>
              <Button variant="outline" onClick={() => setCloseOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button onClick={closeShift} disabled={submitting || !countedCash.trim()}>
                {t('delivery.shifts.confirmClose')}
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Field label={t('delivery.shifts.countedCash', { currency: code })} required>
              <Input
                type="number"
                inputMode="decimal"
                min="0"
                step={step}
                value={countedCash}
                onChange={(e) => setCountedCash(e.target.value)}
                autoFocus
              />
            </Field>

            {/*
              Shown BEFORE submission, not only after — a driver about to hand
              over a shift short of cash sees that here, not from a manager's
              screen five minutes later. `varianceMinor === 0` is drawn green;
              anything else is drawn plainly, in the sign it actually has.
            */}
            <dl className="space-y-2 rounded-lg border p-4 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-ink-muted">{t('delivery.shifts.expected')}</dt>
                <dd className="tabular-nums">
                  <Money amount={expected} branchId={shift.branchId} />
                </dd>
              </div>
              <div className="flex items-center justify-between border-t pt-2 font-semibold">
                <dt>{t('delivery.shifts.variance')}</dt>
                <dd className={varianceTone(varianceMinor)}>
                  <Money amount={fromMinor(varianceMinor, code)} branchId={shift.branchId} />
                </dd>
              </div>
              <p className="text-xs text-ink-muted">
                {varianceMinor === 0
                  ? t('delivery.shifts.varianceNone')
                  : varianceMinor > 0
                    ? t('delivery.shifts.varianceOver')
                    : t('delivery.shifts.varianceShort')}
              </p>
            </dl>

            <Field label={t('delivery.shifts.notes')}>
              <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
          </div>
        </Dialog>
      )}
    </div>
  );
}
